import { Link } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useMyDeliveries, type NotificationDelivery } from '@/hooks/useNotifications.ts';
import { RelativeTime } from '@/components/RelativeTime.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { CATEGORY_LABELS, CHANNEL_LABELS, statusTone } from './deliveryStatus.ts';

/**
 * What the router decided about you, newest first.
 *
 * This is the surface that answers "why didn't I hear about that?", and until it
 * existed the answer was unreachable: every outcome the router records — grouped into
 * a summary, collapsed by a storm, held for a digest, skipped because you caused it —
 * was written to a table with no reader. The vocabulary existed in the backend view
 * helper and appeared nowhere a user could see.
 *
 * **A rule that simply did not match writes no row.** The log would otherwise grow with
 * every rule anyone ever wrote. "My rule should have matched and didn't" is a different
 * question with a different, more expensive answer — the explain drawer on the incident
 * itself, which re-runs the matcher.
 */

function DeliveryRow({ delivery }: { delivery: NotificationDelivery }) {
  const channel = CHANNEL_LABELS[delivery.channel] ?? delivery.channel;
  const category = delivery.eventCategory ? CATEGORY_LABELS[delivery.eventCategory] : null;

  const title = delivery.eventTitle ?? 'A notification';
  const titleNode = delivery.eventLink ? (
    <Link to={delivery.eventLink} className="hover:underline">
      {title}
    </Link>
  ) : (
    title
  );

  return (
    <div className="rounded-md border border-border-subtle px-3 py-2 space-y-1">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm truncate">{titleNode}</p>
          <p className="text-xs text-muted-foreground">
            {channel}
            {/* The address only appears on your own rows — the API withholds it
                otherwise, and this log is your own history, so it is always present
                when there is one to show. */}
            {delivery.destinationLabel ? ` → ${delivery.destinationLabel}` : ''}
            {category ? ` · ${category}` : ''}
          </p>
        </div>
        <span className="flex items-center gap-2 shrink-0">
          <Badge variant={statusTone(delivery.status)} className="font-normal">
            {delivery.statusLabel}
          </Badge>
          <RelativeTime value={delivery.createdAt} className="text-xs text-muted-foreground" />
        </span>
      </div>

      {/* The router's own error text. Present only on your own rows, because the
          transport quotes the recipient address in its failures. */}
      {delivery.detail ? <p className="text-xs text-destructive">{delivery.detail}</p> : null}

      {delivery.attemptCount > 1 ? (
        <p className="text-xs text-muted-foreground">{delivery.attemptCount} attempts.</p>
      ) : null}
    </div>
  );
}

export function NotificationDeliveryLog({ eventId }: { eventId?: string | null } = {}) {
  const { data: deliveries, isLoading } = useMyDeliveries(50, eventId);

  return (
    <section className="bg-card border border-border rounded-lg p-5 space-y-3">
      {/* No heading of its own: the page above supplies one on both branches. What
          stays is the caveat, which is the part a title cannot carry — the log records
          decisions, and a rule that never matched made no decision to record. */}
      <p className="text-xs text-muted-foreground">
        {eventId
          ? 'One row per channel the router considered for this notification. A channel you have no rule for is absent — it was never considered.'
          : "A rule that simply didn't match isn't recorded here; open an incident to ask why one of your rules stayed quiet about it."}
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (deliveries ?? []).length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {eventId
            ? 'Nothing recorded for that notification.'
            : 'Nothing yet. Notifications appear here once something happens that one of your rules matches.'}
        </p>
      ) : (
        <div className="space-y-2">
          {(deliveries ?? []).map((d) => (
            <DeliveryRow key={d.id} delivery={d} />
          ))}
        </div>
      )}
    </section>
  );
}
